import moment from "moment";
import { getAuthenticatedFetch } from "../../msal/authenticatedFetchService";
import { PeoplePlannerDisableInfoModel } from "../types/PeoplePlannerDisableInfoModel";
import { PeoplePlannerDisableInfoDTO } from "../types/PeoplePlannerDisableInfoDTO";
import { CareRosteringEnableInfoModel } from "../types/CareRosteringEnableInfoModel";
import { CareRosteringEnableInfoDTO } from "../types/CareRosteringEnableInfoDTO";
import { CareConnectConfigurationModel } from "../types/CareConnectConfigurationModel";
import { CareConnectConfigurationDTO } from "../types/CareConnectConfigurationDTO";

class CustomerService {
    private addEnvironmentName(url: string, environmentName?: string) {
        if (!environmentName) {
            return url;
        }

        return `${url}${url.includes('?') ? '&' : '?'}environmentName=${encodeURIComponent(environmentName)}`;
    }

    getCustomerNameFromCustomerNamingConvention(customerNamingConvention: string, customerName: string) {
        if (!customerName)
            return '';
        
        return customerNamingConvention.replaceAll('{CustomerName}', customerName);
    }
    
    async getAllCustomers(environmentName?: string) {
        let url = this.addEnvironmentName(`${process.env.REACT_APP_SELF_SERVICE_PEOPLEPLANNER_API_URL}/Customer`, environmentName);
        let response = await getAuthenticatedFetch()(url);
        return await response.json();
    }

    async getCustomerByClientId(clientId: number, environmentName?: string) {
        let url = this.addEnvironmentName(`${process.env.REACT_APP_SELF_SERVICE_PEOPLEPLANNER_API_URL}/Customer/${clientId}`, environmentName);
        let response = await getAuthenticatedFetch()(url);
        return await response.json();
    }

    async getDbSizes(clientId: number, environmentName?: string) {
        let url = this.addEnvironmentName(`${process.env.REACT_APP_SELF_SERVICE_PEOPLEPLANNER_API_URL}/Customer/${clientId}/dbsizes`, environmentName);
        let response = await getAuthenticatedFetch()(url);

        if (!response.ok) {
            throw new Error();
        }

        return await response.json();
    }

    async resetServiceBroker(clientId: number, environmentName?: string) {
        const response = await getAuthenticatedFetch()(this.addEnvironmentName(`${process.env.REACT_APP_SELF_SERVICE_PEOPLEPLANNER_API_URL}/Customer/${clientId}/resetservicebroker`, environmentName), {
            method: 'POST'
        });

        if (!response.ok) {
            throw new Error();
        }

        return response;
    }

    async findEmployeeById(clientId: number, employeeId: string, environmentName?: string) {
        return await this.findWorkspaceLink(this.addEnvironmentName(`${process.env.REACT_APP_SELF_SERVICE_PEOPLEPLANNER_API_URL}/Customer/${clientId}/employee/find?employeeId=${encodeURIComponent(employeeId)}`, environmentName));
    }

    async findEmployeesByName(clientId: number, firstName: string, lastName: string, environmentName?: string) {
        const params = new URLSearchParams();
        if (firstName) params.append('firstName', firstName);
        if (lastName) params.append('lastName', lastName);

        return await this.findWorkspaceLink(this.addEnvironmentName(`${process.env.REACT_APP_SELF_SERVICE_PEOPLEPLANNER_API_URL}/Customer/${clientId}/employee/find?${params.toString()}`, environmentName));
    }

    async unlinkEmployee(clientId: number, employeeId: string, environmentName?: string) {
        const response = await getAuthenticatedFetch()(this.addEnvironmentName(`${process.env.REACT_APP_SELF_SERVICE_PEOPLEPLANNER_API_URL}/Customer/${clientId}/employee/unlink`, environmentName), {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({employeeId})
        });

        if (!response.ok) {
            throw new Error();
        }

        return response;
    }

    async findUserById(clientId: number, userId: string, environmentName?: string) {
        return await this.findWorkspaceLink(this.addEnvironmentName(`${process.env.REACT_APP_SELF_SERVICE_PEOPLEPLANNER_API_URL}/Customer/${clientId}/user/find?userId=${encodeURIComponent(userId)}`, environmentName));
    }

    async findUsersByUsername(clientId: number, username: string, environmentName?: string) {
        return await this.findWorkspaceLink(this.addEnvironmentName(`${process.env.REACT_APP_SELF_SERVICE_PEOPLEPLANNER_API_URL}/Customer/${clientId}/user/find?username=${encodeURIComponent(username)}`, environmentName));
    }

    async unlinkUser(clientId: number, userInformationId: string, environmentName?: string) {
        const response = await getAuthenticatedFetch()(this.addEnvironmentName(`${process.env.REACT_APP_SELF_SERVICE_PEOPLEPLANNER_API_URL}/Customer/${clientId}/user/unlink`, environmentName), {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({userInformationId})
        });

        if (response.status === 404) {
            return null;
        }

        if (!response.ok) {
            throw new Error();
        }

        return response;
    }

    private async findWorkspaceLink(url: string) {
        const response = await getAuthenticatedFetch()(url);

        if (response.status === 404) {
            return null;
        }

        if (!response.ok) {
            throw new Error();
        }

        return await response.json();
    }

    async requestCreateAppCustomer(companyName: string, countryId: number, environmentType: number, environmentName?: string) {
        const response = await getAuthenticatedFetch()(this.addEnvironmentName(`${process.env.REACT_APP_SELF_SERVICE_PEOPLEPLANNER_API_URL}/Customer/createcustomerrequest`, environmentName), {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({
                countryId,
                companyName,
                environmentType
            })
        });

        if (!response.ok) {
            throw new Error();
        }

        return response.json();
    }

    async createAppCustomerStatus(taskId: string) {
        let url = `${process.env.REACT_APP_SELF_SERVICE_PEOPLEPLANNER_API_URL}/Customer/createcustomerstatus/${taskId}`;
        let response = await getAuthenticatedFetch()(url);

        if (!response.ok) {
            throw new Error();
        }

        return response.json();
    }
    
    async deleteCustomer(clientId: number, environmentName?: string) {
        const response = await getAuthenticatedFetch()(this.addEnvironmentName(`${process.env.REACT_APP_SELF_SERVICE_PEOPLEPLANNER_API_URL}/Customer/${clientId}/delete`, environmentName), {
            method: 'POST'
        });

        const data = await response.json();

        return data;
    }
    
    async requestlockAppCustomer(clientId: number, salesForceId: string, environmentName?: string) {
        const response = await getAuthenticatedFetch()(this.addEnvironmentName(`${process.env.REACT_APP_SELF_SERVICE_PEOPLEPLANNER_API_URL}/Customer/${clientId}/applockrequest`, environmentName), {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({
                salesForceId: salesForceId
            })
        });
        
        if (!response.ok) {
            throw new Error();
        }

        return response.json();
    }

    async lockAppCustomerStatus(taskId: string) {
        let url = `${process.env.REACT_APP_SELF_SERVICE_PEOPLEPLANNER_API_URL}/Customer/applockstatus/${taskId}`;
        let response = await getAuthenticatedFetch()(url);

        if (!response.ok) {
            throw new Error();
        }

        return response.json();
    }

    async getAppCustomerTerminations() {
        let url = `${process.env.REACT_APP_SELF_SERVICE_PEOPLEPLANNER_API_URL}/Customer/appcustomerterminations`;
        let response = await getAuthenticatedFetch()(url);

        if (!response.ok) {
            throw new Error();
        }

        return response.json();
    }

    async unlockAppCustomer(environmentName: string, terminationsId: string) {
        let url = `${process.env.REACT_APP_SELF_SERVICE_PEOPLEPLANNER_API_URL}/Customer/appunlockcustomer`;
        let response = await getAuthenticatedFetch()(url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({
                environmentName,
                terminationsId
            })
        });
        
        if (!response.ok) {
            throw new Error();
        }

        return response;
    }

    async moveCustomer(clientId: number, appGrpName: string, environmentName?: string) {
        const response = await getAuthenticatedFetch()(this.addEnvironmentName(`${process.env.REACT_APP_SELF_SERVICE_PEOPLEPLANNER_API_URL}/Customer/${clientId}/move/${appGrpName}`, environmentName), {
            method: 'POST'
        });

        const data = await response.json();

        if (!response.ok) {
            throw new Error();
        }

        return data;
    }

    async getMoveCustomerStatus(taskId: string) {
        let url = `${process.env.REACT_APP_SELF_SERVICE_PEOPLEPLANNER_API_URL}/Customer/movecustomerstatus/${taskId}`;
        let response = await getAuthenticatedFetch()(url);

        if (!response.ok) {
            throw new Error();
        }

        return response.json();
    }

    async getLastSignInDateTime(clientId: number, environmentName?: string) {
        let url = this.addEnvironmentName(`${process.env.REACT_APP_SELF_SERVICE_PEOPLEPLANNER_API_URL}/Customer/${clientId}/lastsignindatetime`, environmentName);
        let response = await getAuthenticatedFetch()(url);
        return await response.json();
    }

    async getPeoplePlannerDisableInfo(clientId: number, isOldPeoplePlanner: boolean, environmentName?: string): Promise<PeoplePlannerDisableInfoDTO> {
        let url = this.addEnvironmentName(`${process.env.REACT_APP_SELF_SERVICE_PEOPLEPLANNER_API_URL}/Customer/${clientId}/peopleplannerdisableinfo?isOldPeoplePlanner=${isOldPeoplePlanner}`, environmentName);
        let response = await getAuthenticatedFetch()(url);

        if (!response.ok) {
            throw new Error("Failed to fetch People Planner Disable Info");
        }
        
        const disableData = await response.json() as PeoplePlannerDisableInfoDTO;

        if (disableData.disablePeoplePlannerDate) {
            const utcDate = moment.utc(disableData.disablePeoplePlannerDate); 
            const localDate = utcDate.local();
            disableData.disablePeoplePlannerDate = localDate.toDate();
        }

        return disableData;
    }

    async updatePeoplePlannerDisableInfo(clientId: number, disablePeoplePlannerInfo: PeoplePlannerDisableInfoModel, environmentName?: string) {
        const response = await getAuthenticatedFetch()(this.addEnvironmentName(`${process.env.REACT_APP_SELF_SERVICE_PEOPLEPLANNER_API_URL}/Customer/${clientId}/peopleplannerdisable`, environmentName), {
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({
                isOldPeoplePlanner: disablePeoplePlannerInfo.isOldPeoplePlanner,
                disablePeoplePlanner: disablePeoplePlannerInfo.disablePeoplePlanner,
                disablePeoplePlannerDate: disablePeoplePlannerInfo.disablePeoplePlannerDate
            })           
        });

        if (!response.ok) {
            const errorMessage = await response.text();
            throw new Error(`Failed to update People Planner Disable Info: ${errorMessage}`);
          }
    }

    async getCareRosteringEnableInfo(clientId: number, environmentName?: string): Promise<CareRosteringEnableInfoDTO> {
        let url = this.addEnvironmentName(`${process.env.REACT_APP_SELF_SERVICE_PEOPLEPLANNER_API_URL}/Customer/${clientId}/carerosteringenableinfo`, environmentName);
        let response = await getAuthenticatedFetch()(url);

        if (!response.ok) {
            throw new Error("Failed to fetch Care Rostering Enable Info");
        }
        
        const enableData = await response.json() as CareRosteringEnableInfoDTO;

        return enableData;
    }

    async restoreToSupportServer(clientId: number, includeAuditDb: boolean, includeExternalDb: boolean, reason: string, environmentName?: string) {
        const response = await getAuthenticatedFetch()(this.addEnvironmentName(`${process.env.REACT_APP_SELF_SERVICE_PEOPLEPLANNER_API_URL}/RestoreEnv/${clientId}/restoretosupportserver`, environmentName), {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({
                clientId,
                includeAuditDb,
                includeExternalDb,
                reason
            })
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => null);
            throw new Error(errorData?.errorMessage || "Failed to trigger restore to support server pipeline");
        }

        return response.json();
    }

    async retrieveDbBackups(clientId: number, includeAuditDb: boolean, includeExternalDb: boolean, reason: string, environmentName?: string) {
        const response = await getAuthenticatedFetch()(this.addEnvironmentName(`${process.env.REACT_APP_SELF_SERVICE_PEOPLEPLANNER_API_URL}/RestoreEnv/${clientId}/retrievedbbackups`, environmentName), {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({
                clientId,
                includeAuditDb,
                includeExternalDb,
                reason
            })
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => null);
            throw new Error(errorData?.errorMessage || "Failed to trigger retrieve DB backups pipeline");
        }

        return response.json();
    }

    async getRetrieveDbBackupsAvailable(environmentName?: string): Promise<boolean> {
        const url = this.addEnvironmentName(`${process.env.REACT_APP_SELF_SERVICE_PEOPLEPLANNER_API_URL}/RestoreEnv/retrievedbbackupsavailable`, environmentName);
        const response = await getAuthenticatedFetch()(url);

        if (!response.ok) {
            return false;
        }

        return response.json();
    }

    async getIsCchCustomer(clientId: number, environmentName?: string): Promise<boolean> {
        const url = this.addEnvironmentName(`${process.env.REACT_APP_SELF_SERVICE_PEOPLEPLANNER_API_URL}/RestoreEnv/${clientId}/iscchcustomer`, environmentName);
        const response = await getAuthenticatedFetch()(url);

        if (!response.ok) {
            return false;
        }

        return response.json();
    }

    async getRestoreToSupportServerAvailable(environmentName?: string): Promise<boolean> {
        const url = this.addEnvironmentName(`${process.env.REACT_APP_SELF_SERVICE_PEOPLEPLANNER_API_URL}/RestoreEnv/restoretosupportserveravailable`, environmentName);
        const response = await getAuthenticatedFetch()(url);

        if (!response.ok) {
            return false;
        }

        return response.json();
    }

    async updateCareRosteringEnableInfo(clientId: number, enableCareRosteringInfo: CareRosteringEnableInfoModel, environmentName?: string) {
        const response = await getAuthenticatedFetch()(this.addEnvironmentName(`${process.env.REACT_APP_SELF_SERVICE_PEOPLEPLANNER_API_URL}/Customer/${clientId}/carerosteringenable`, environmentName), {
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({
                enableCareRostering: enableCareRosteringInfo.enableCareRostering
            })           
        });

        if (!response.ok) {
            const errorMessage = await response.text();
            throw new Error(`Failed to update Care Rostering Enable Info: ${errorMessage}`);
          }
    }

    async getCareConnectConfiguration(clientId: number, environmentName?: string): Promise<CareConnectConfigurationDTO> {
        const url = this.addEnvironmentName(`${process.env.REACT_APP_SELF_SERVICE_PEOPLEPLANNER_API_URL}/Customer/${clientId}/careconnectconfiguration`, environmentName);
        const response = await getAuthenticatedFetch()(url);

        if (!response.ok) {
            throw new Error("Failed to fetch Care Connect configuration");
        }

        return await response.json() as CareConnectConfigurationDTO;
    }

    async updateCareConnectConfiguration(clientId: number, careConnectConfiguration: CareConnectConfigurationModel, environmentName?: string) {
        const response = await getAuthenticatedFetch()(this.addEnvironmentName(`${process.env.REACT_APP_SELF_SERVICE_PEOPLEPLANNER_API_URL}/Customer/${clientId}/careconnectconfiguration`, environmentName), {
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({
                isLiteEdition: careConnectConfiguration.isLiteEdition,
                dailyEmailProcessingLimit: careConnectConfiguration.dailyEmailProcessingLimit
            })
        });

        if (!response.ok) {
            const errorMessage = await response.text();
            throw new Error(`Failed to update Care Connect configuration: ${errorMessage}`);
        }
    }
}

export default new CustomerService();
